type MarkerShape = "dot" | "ring" | "wide";

type Marker = { x: number; y: number; shape: MarkerShape };

/* A single schematic mid-sagittal head profile is reused for every phoneme —
   only the highlighted articulation zone changes. Legible and consistent
   beats anatomically precise for a training aid like this. */
const MARKERS: Record<string, Marker> = {
  lips: { x: 92, y: 58, shape: "dot" },
  "lips-round": { x: 92, y: 58, shape: "ring" },
  labiodental: { x: 87, y: 65, shape: "dot" },
  dental: { x: 82, y: 60, shape: "dot" },
  "tip-alveolar": { x: 74, y: 49, shape: "dot" },
  "tip-retroflex": { x: 67, y: 43, shape: "dot" },
  palatal: { x: 56, y: 37, shape: "dot" },
  "back-velar": { x: 38, y: 33, shape: "dot" },
  glottal: { x: 21, y: 68, shape: "dot" },
  "vowel-round": { x: 92, y: 58, shape: "ring" },
  "vowel-open": { x: 84, y: 72, shape: "wide" },
};

const FALLBACK_MARKER: Marker = { x: 74, y: 49, shape: "dot" };

type ArticulatorDiagramProps = { id: string; size?: number };

export function ArticulatorDiagram({ id, size = 96 }: ArticulatorDiagramProps) {
  const marker = MARKERS[id] ?? FALLBACK_MARKER;

  return (
    <svg
      class="articulator-diagram"
      width={size}
      height={size}
      viewBox="0 0 120 120"
      aria-hidden="true"
      fill="none"
    >
      {/* head/forehead/nose/chin profile, facing right */}
      <path
        d="M 14 60
           C 14 34, 34 12, 62 10
           C 78 9, 86 18, 88 28
           C 96 30, 100 38, 98 46
           L 94 50
           C 96 54, 95 58, 91 60
           L 96 66
           C 90 74, 80 84, 66 90
           C 56 94, 44 95, 36 92
           C 24 88, 16 76, 14 60 Z"
        stroke="var(--fg-faint)"
        stroke-width="2"
      />
      {/* roof of the mouth: lips -> alveolar ridge -> hard palate -> velum -> throat */}
      <path
        d="M 91 60 C 80 52, 66 46, 56 40 C 46 36, 38 34, 28 36 C 22 38, 20 46, 21 55"
        stroke="var(--fg-faint)"
        stroke-width="1.5"
        stroke-dasharray="1 4"
        stroke-linecap="round"
      />
      {/* tongue */}
      <path
        d="M 24 68 C 30 78, 46 82, 60 78 C 72 75, 80 68, 84 62"
        stroke="var(--fg-faint)"
        stroke-width="2"
        stroke-linecap="round"
      />
      {/* marker */}
      {marker.shape === "dot" && <circle cx={marker.x} cy={marker.y} r={5} fill="var(--accent)" />}
      {marker.shape === "ring" && (
        <circle cx={marker.x} cy={marker.y} r={6} fill="none" stroke="var(--accent)" stroke-width="3" />
      )}
      {marker.shape === "wide" && (
        <path
          d={`M ${marker.x - 10} ${marker.y - 6} Q ${marker.x} ${marker.y + 10} ${marker.x + 8} ${marker.y - 4}`}
          stroke="var(--accent)"
          stroke-width="3"
          stroke-linecap="round"
        />
      )}
    </svg>
  );
}
